import { nextTick, watch, type Ref } from "vue";
import { useRoute } from "vue-router";

import NewReportMetrics from "@/components/datascience/ReportMetrics.vue";
import TableReport, { type TableReportState } from "@/components/datascience/TableReport.vue";
import TwoColumnsLayout from "@/layouts/TwoColumnsLayout.vue";
import ReportModelPipeline from "@/views/reports/ReportModelPipeline.vue";

interface UseReportScrollingOptions {
  twoColumnsLayoutRef: Ref<InstanceType<typeof TwoColumnsLayout> | null>;
  tableReportRef?: Ref<InstanceType<typeof TableReport> | null>;
  reportMetricsRef?: Ref<InstanceType<typeof NewReportMetrics> | null>;
  reportModelPipelineRef?: Ref<InstanceType<typeof ReportModelPipeline> | null>;
  staticAnchors?: string[];
  scrollDuration?: number;
}

const DEFAULT_STATIC_ANCHORS = ["split-selector", "table-report", "general-metrics", "pipeline"];
const DEFAULT_SCROLL_DURATION_MS = 200;

export function useReportScrolling(options: UseReportScrollingOptions) {
  const {
    twoColumnsLayoutRef,
    tableReportRef,
    reportMetricsRef,
    reportModelPipelineRef,
    staticAnchors = DEFAULT_STATIC_ANCHORS,
    scrollDuration = DEFAULT_SCROLL_DURATION_MS,
  } = options;

  const route = useRoute();

  function smoothScrollTo(element: HTMLElement, target: number, duration: number): Promise<void> {
    return new Promise((resolve) => {
      const start = element.scrollTop;
      const distance = target - start;

      // Resolve immediately if already at target position
      if (Math.abs(distance) < 1) {
        resolve();
        return;
      }

      const startTime = performance.now();

      function easeOutCubic(t: number): number {
        return 1 - Math.pow(1 - t, 3);
      }

      function step(currentTime: number) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        element.scrollTop = start + distance * easeOutCubic(progress);

        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      }

      requestAnimationFrame(step);
    });
  }

  async function scrollToElement(element: HTMLElement): Promise<void> {
    const scrollElement = twoColumnsLayoutRef.value?.rightColumnScroller?.$el?.querySelector(
      ".simplebar-content-wrapper"
    ) as HTMLElement | null;
    if (scrollElement) {
      const targetTop = element.offsetTop;
      await smoothScrollTo(scrollElement, targetTop, scrollDuration);
    }
  }

  function highlightElement(element: HTMLElement) {
    element.classList.add("blink-border");
    setTimeout(() => {
      element.classList.remove("blink-border");
    }, 8 * scrollDuration);
  }

  async function scrollToAnchor(hash: string) {
    if (!hash || !twoColumnsLayoutRef.value) {
      return;
    }

    const elementId = hash.replace("#", "");

    // Decide which card the anchor belongs to and what needs to happen before scrolling
    let cardContainerId: string;
    let needsExpand = false;
    let needsTableStateChange = false;
    let nextTableState: TableReportState | null = null;

    if (staticAnchors.includes(elementId)) {
      // Anchors pointing to the non‑expandable part of a card
      cardContainerId = elementId;
    } else if (elementId === "inspection") {
      // Inside the expandable content of the pipeline card
      cardContainerId = "pipeline";
      needsExpand = true;
    } else if (elementId.startsWith("inspection-")) {
      // Inside the expandable content of the pipeline card
      cardContainerId = "pipeline";
      needsExpand = true;
    } else if (elementId.startsWith("table-report-")) {
      // Table report sub‑sections (table / stats / associations) – non‑expandable part,
      // but we must change the internal state first.
      cardContainerId = "table-report";
      needsTableStateChange = true;
      nextTableState = elementId.replace("table-report-", "") as TableReportState;
    } else {
      // Any other anchor is a metric plot inside the metrics card's expandable content
      cardContainerId = "general-metrics";
      needsExpand = true;
    }

    // Apply state changes / expansions before the final scroll when needed
    if (needsTableStateChange && nextTableState && tableReportRef?.value) {
      tableReportRef.value.selectState(nextTableState);
    }
    if (needsExpand) {
      if (cardContainerId === "pipeline") {
        await reportModelPipelineRef?.value?.expand();
      } else if (cardContainerId === "general-metrics") {
        await reportMetricsRef?.value?.expand();
      }
      await nextTick();
    } else if (needsTableStateChange) {
      await nextTick();
    }

    const cardContainer = document.getElementById(cardContainerId);
    if (!cardContainer) {
      return;
    }

    const anchorElement = document.getElementById(elementId);

    // Scroll to the specific anchor when it exists (e.g. inspection section), so text
    // blocks and plots behave the same
    if (anchorElement) {
      await scrollToElement(anchorElement);
      highlightElement(anchorElement);
    } else {
      await scrollToElement(cardContainer);
      const toHHighlightElement = cardContainer.querySelector(".card-content");
      if (toHHighlightElement) {
        highlightElement(toHHighlightElement as HTMLElement);
      }
    }
  }

  // Watch for hash changes (but not on initial mount - let components handle that)
  watch(
    () => route.hash,
    (hash, oldHash) => {
      // Only scroll on hash changes, not on initial mount (when oldHash is undefined)
      if (oldHash !== undefined && hash) {
        scrollToAnchor(hash);
      }
    }
  );

  return {
    scrollToAnchor,
  };
}
